'use client';

import { useLetterWritingPreferencesUpdate } from '@sola/my-sdk/settings/hooks.ts';
import { GraphQLWritingPreference } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { useWritingPreferences } from '@sola/supporter-sdk/hooks/settings.ts';

export enum WritingPreferenceChannel {
  Email = 'email',
  Print = 'print',
}

export type LetterWritingFormValues = {
  email: { email: boolean };
  print: { print: boolean };
};

/**
 * Map letter-writing form values to the `writingPreferences` enum array
 * (Email → `Electronic`, Print → `DigitalPrint`).
 * @param values The letter-writing form values.
 * @returns The `writingPreferences` array in stable order, no duplicates.
 */
export const deriveWritingPreferences = (
  values: LetterWritingFormValues,
): GraphQLWritingPreference[] => {
  const writingPreferences: GraphQLWritingPreference[] = [];
  if (values.email?.email) {
    writingPreferences.push(GraphQLWritingPreference.Electronic);
  }
  if (values.print?.print) {
    writingPreferences.push(GraphQLWritingPreference.DigitalPrint);
  }
  return writingPreferences;
};

export const useLetterWritingPreferencesLoadState = () => {
  const { isError, isLoading, isPending } = useWritingPreferences();
  const showSkeleton = (isLoading || isPending) && !isError;
  return { isError, showSkeleton };
};

export const useLetterWritingPreferences = () => {
  const { hasPrint, hasEmail } = useWritingPreferences();
  const {
    mutateAsync: save,
    isPending: isSaving,
    error: saveError,
  } = useLetterWritingPreferencesUpdate();

  return {
    hasPrint,
    hasEmail,
    save,
    isSaving,
    saveError,
  };
};
